import { useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import S3Repository from 'repositories/S3Repository'
import type { RootState } from 'store/modules/rootReducer'
import { getLineBearerToken } from 'utils/bearerToken'
import { isAuthenticatedS3URL } from 'utils/s3Authenticated'
import { isPdfUrl } from 'utils/utils'

/**
 * Para PDFs e outros assets que não são `<img>` — devolve `blob:` quando possível.
 */
export function useAuthenticatedBlobUrl (s3Url: string | undefined | null): string {
  const [blobUrl, setBlobUrl] = useState<string>('')
  const reduxToken = useSelector((s: RootState) => s.auth?.token || '')
  const hasToken = !!(getLineBearerToken() || reduxToken)

  useEffect(() => {
    if (!s3Url) {
      setBlobUrl('')
      return
    }

    if (!isAuthenticatedS3URL(s3Url)) {
      setBlobUrl(s3Url)
      return
    }

    if (!hasToken) {
      setBlobUrl('')
      return
    }

    let created = ''
    let cancelled = false

    S3Repository.getImage(s3Url)
      .then((raw) => {
        if (cancelled) return
        let blob = raw
        if (raw.type === 'application/octet-stream') {
          if (isPdfUrl(s3Url)) {
            blob = new Blob([raw], { type: 'application/pdf' })
          } else if (s3Url.endsWith('.image')) {
            blob = new Blob([raw], { type: 'image/png' })
          }
        }
        created = URL.createObjectURL(blob)
        setBlobUrl(created)
      })
      .catch(() => {
        if (!cancelled) setBlobUrl('')
      })

    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [s3Url, hasToken, reduxToken])

  return blobUrl
}
